import { useState, useCallback, useEffect, useRef } from '@wordpress/element';

/* ─── single toast item ─── */
const ToastItem = ({ toast, onRemove }) => {
    const timerRef = useRef(null);

    useEffect(() => {
        timerRef.current = setTimeout(() => onRemove(toast.id), toast.duration || 4000);
        return () => clearTimeout(timerRef.current);
    }, [toast.id]);

    const icons = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };

    return (
        <div className={`cpiw-toast cpiw-toast--${toast.type}`} role="alert">
            <span className="cpiw-toast__icon">{icons[toast.type] || icons.info}</span>
            <span className="cpiw-toast__msg">{toast.message}</span>
            <button className="cpiw-toast__close" onClick={() => onRemove(toast.id)} aria-label="Dismiss">✕</button>
        </div>
    );
};

/* ─── container that renders all toasts ─── */
export const ToastContainer = ({ toasts, onRemove }) => (
    <div className="cpiw-toast-container" aria-live="polite">
        {toasts.map(t => (
            <ToastItem key={t.id} toast={t} onRemove={onRemove} />
        ))}
    </div>
);

/* ─── hook ─── */
let _idCounter = 0;

export const useToast = () => {
    const [toasts, setToasts] = useState([]);

    const remove = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const show = useCallback((message, type = 'info', duration = 4000) => {
        const id = ++_idCounter;
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    const toast = {
        success: (msg, dur)  => show(msg, 'success', dur),
        error:   (msg, dur)  => show(msg, 'error',   dur || 6000),
        warning: (msg, dur)  => show(msg, 'warning', dur),
        info:    (msg, dur)  => show(msg, 'info',    dur),
    };

    return { toasts, toast, removeToast: remove };
};
